import React, { useState, useEffect } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatINRFull, formatStatus, formatDate } from '@/lib/utils';
import { Download, Search, Filter, Package, Shield, AlertTriangle, Users, BarChart3, CheckCircle, Clock, XCircle } from 'lucide-react';

export default function ReportsPage() {
  const [tab, setTab] = useState('tools');

  const tabs = [
    { id: 'tools', label: 'Tools Inventory', icon: Package },
    { id: 'amc', label: 'AMC Compliance', icon: Shield },
    { id: 'safety', label: 'Safety Incidents', icon: AlertTriangle },
    { id: 'hr', label: 'HR / Employee', icon: Users },
    { id: 'po-invoice', label: 'PO / Invoice', icon: BarChart3 },
  ];

  return (
    <div data-testid="reports-page" className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Reports</h1>
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`report-tab-${t.id}`}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 whitespace-nowrap ${
              tab === t.id ? 'border-[#1B5486] text-[#1B5486]' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'tools' && <ToolsInventoryReport />}
      {tab === 'amc' && <AMCComplianceReport />}
      {tab === 'safety' && <SafetyIncidentReport />}
      {tab === 'hr' && <HRReport />}
      {tab === 'po-invoice' && <POInvoiceReport />}
    </div>
  );
}

function exportToCSV(data, filename) {
  if (!data.length) return;
  const headers = Object.keys(data[0]);
  const csv = [headers.join(','), ...data.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
}

function SummaryCard({ label, value, icon: Icon, color = 'text-slate-900' }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <p className="text-xs text-slate-500">{label}</p>
      </div>
      <p className={`text-lg font-semibold ${color}`} data-testid={`summary-${label.toLowerCase().replace(/\s+/g,'-')}`}>{value}</p>
    </div>
  );
}

// RPT-01: Tools Inventory Report
function ToolsInventoryReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    api.get('/reports/tools-inventory').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!data) return <Empty />;

  const items = (data.items || []).filter(i => {
    if (filterCat && i.item_name !== filterCat) return false;
    if (filterStatus && i.status !== filterStatus) return false;
    return true;
  });
  const categories = [...new Set((data.items || []).map(i => i.item_name))];

  return (
    <div className="space-y-4" data-testid="tools-inventory-report">
      <div className="grid grid-cols-3 gap-3">
        <SummaryCard label="Total Items" value={data.summary.total} icon={Package} />
        <SummaryCard label="Issued" value={data.summary.issued} icon={Clock} color="text-amber-600" />
        <SummaryCard label="Returned" value={data.summary.returned} icon={CheckCircle} color="text-emerald-600" />
      </div>

      <div className="flex gap-3">
        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-tool-cat">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none">
          <option value="">All Status</option>
          <option value="ISSUED">Issued</option><option value="RETURNED">Returned</option>
        </select>
        <button onClick={() => exportToCSV(items, 'tools_inventory.csv')} data-testid="export-tools-csv"
          className="ml-auto text-sm text-emerald-600 hover:underline flex items-center gap-1"><Download size={14} /> Export Excel</button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead><tr className="border-b border-slate-100 bg-slate-50/50">
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Item</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Employee</th>
            <th className="text-right py-2.5 px-4 text-xs uppercase text-slate-500">Qty</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Issue Date</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Condition</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Return Date</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Status</th>
          </tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id} className="border-b border-slate-50">
                <td className="py-2 px-4 font-medium">{i.item_name}</td>
                <td className="py-2 px-4 text-slate-600">{i.employee_name}</td>
                <td className="py-2 px-4 text-right">{i.quantity}</td>
                <td className="py-2 px-4">{formatDate(i.issue_date)}</td>
                <td className="py-2 px-4 text-xs">{i.condition}</td>
                <td className="py-2 px-4">{i.return_date ? formatDate(i.return_date) : '-'}</td>
                <td className="py-2 px-4"><span className={`text-xs px-1.5 py-0.5 rounded ${i.status === 'RETURNED' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>{i.status}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {items.length === 0 && <div className="text-center py-12 text-slate-400">No items found</div>}
      </div>
    </div>
  );
}

// RPT-02: AMC Compliance Report
function AMCComplianceReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterTraffic, setFilterTraffic] = useState('');

  useEffect(() => {
    api.get('/reports/amc-compliance').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!data) return <Empty />;

  const items = (data.items || []).filter(i => !filterTraffic || i.traffic === filterTraffic);

  const trafficColor = (t) => t === 'GREEN' ? 'bg-emerald-500' : t === 'YELLOW' ? 'bg-amber-400' : 'bg-red-500';
  const trafficBadge = (t) => t === 'GREEN' ? 'bg-emerald-100 text-emerald-700' : t === 'YELLOW' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';

  return (
    <div className="space-y-4" data-testid="amc-compliance-report">
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Total Contracts" value={data.summary.total} icon={Shield} />
        <SummaryCard label="Green" value={data.summary.green} icon={CheckCircle} color="text-emerald-600" />
        <SummaryCard label="Yellow" value={data.summary.yellow} icon={Clock} color="text-amber-600" />
        <SummaryCard label="Red" value={data.summary.red} icon={XCircle} color="text-red-600" />
      </div>

      <div className="flex gap-3">
        <select value={filterTraffic} onChange={e => setFilterTraffic(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-traffic">
          <option value="">All Status</option>
          <option value="GREEN">Green</option><option value="YELLOW">Yellow</option><option value="RED">Red</option>
        </select>
        <button onClick={() => exportToCSV(items, 'amc_compliance.csv')} data-testid="export-amc-csv"
          className="ml-auto text-sm text-emerald-600 hover:underline flex items-center gap-1"><Download size={14} /> Export Excel</button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead><tr className="border-b border-slate-100 bg-slate-50/50">
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Project</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Client</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Status</th>
            <th className="text-right py-2.5 px-4 text-xs uppercase text-slate-500">PM Visits</th>
            <th className="text-right py-2.5 px-4 text-xs uppercase text-slate-500">Completion</th>
            <th className="text-center py-2.5 px-4 text-xs uppercase text-slate-500">Traffic</th>
          </tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.project_id} className="border-b border-slate-50">
                <td className="py-2 px-4"><span className="font-medium text-[#1B5486]">{i.project_code}</span> <span className="text-slate-600 text-xs">{i.project_name}</span></td>
                <td className="py-2 px-4 text-slate-600">{i.client_name}</td>
                <td className="py-2 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(i.status)}</span></td>
                <td className="py-2 px-4 text-right">{i.approved_dwrs}/{i.expected_visits}</td>
                <td className="py-2 px-4 text-right">
                  <div className="flex items-center gap-2 justify-end">
                    <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${trafficColor(i.traffic)}`} style={{width: `${Math.min(i.completion_percent, 100)}%`}} />
                    </div>
                    <span className="text-xs font-medium">{i.completion_percent}%</span>
                  </div>
                </td>
                <td className="py-2 px-4 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${trafficBadge(i.traffic)}`} data-testid={`traffic-${i.project_id}`}>{i.traffic}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {items.length === 0 && <div className="text-center py-12 text-slate-400">No AMC contracts found</div>}
      </div>
    </div>
  );
}

// RPT-03: Safety Incident Report
function SafetyIncidentReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('');

  useEffect(() => {
    api.get('/reports/safety-incidents').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!data) return <Empty />;

  const items = (data.items || []).filter(i => !filterSeverity || i.severity === filterSeverity);
  const sevColors = { HIGH: 'bg-red-100 text-red-700', MEDIUM: 'bg-amber-100 text-amber-700', LOW: 'bg-blue-100 text-blue-700' };

  return (
    <div className="space-y-4" data-testid="safety-incident-report">
      <div className="grid grid-cols-4 gap-3">
        <SummaryCard label="Total Incidents" value={data.summary.total} icon={AlertTriangle} />
        <SummaryCard label="High Severity" value={data.summary.by_severity?.HIGH || 0} icon={XCircle} color="text-red-600" />
        <SummaryCard label="Medium" value={data.summary.by_severity?.MEDIUM || 0} icon={Clock} color="text-amber-600" />
        <SummaryCard label="Low" value={data.summary.by_severity?.LOW || 0} icon={CheckCircle} color="text-blue-600" />
      </div>

      {/* Monthly trend */}
      {data.monthly_trend?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="monthly-trend">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Monthly Trend</h3>
          <div className="flex items-end gap-2 h-24">
            {data.monthly_trend.map(m => {
              const maxCount = Math.max(...data.monthly_trend.map(x => x.count), 1);
              const pct = (m.count / maxCount) * 100;
              return (
                <div key={m.month} className="flex-1 flex flex-col items-center">
                  <span className="text-[10px] text-slate-600 font-medium mb-1">{m.count}</span>
                  <div className="w-full bg-red-400 rounded-t" style={{height: `${Math.max(pct, 5)}%`}} />
                  <span className="text-[9px] text-slate-400 mt-1">{m.month.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-severity">
          <option value="">All Severity</option>
          <option value="HIGH">High</option><option value="MEDIUM">Medium</option><option value="LOW">Low</option>
        </select>
        <button onClick={() => exportToCSV(items, 'safety_incidents.csv')} data-testid="export-safety-csv"
          className="ml-auto text-sm text-emerald-600 hover:underline flex items-center gap-1"><Download size={14} /> Export</button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead><tr className="border-b border-slate-100 bg-slate-50/50">
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Date</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Project</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Description</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Severity</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Status</th>
          </tr></thead>
          <tbody>
            {items.map(i => (
              <tr key={i.id} className="border-b border-slate-50">
                <td className="py-2 px-4">{formatDate(i.date)}</td>
                <td className="py-2 px-4 text-[#1B5486]">{i.project_code || '-'}</td>
                <td className="py-2 px-4 text-slate-700 max-w-[250px] truncate">{i.description || '-'}</td>
                <td className="py-2 px-4"><span className={`text-xs px-1.5 py-0.5 rounded ${sevColors[i.severity] || 'bg-slate-100 text-slate-600'}`}>{i.severity}</span></td>
                <td className="py-2 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{formatStatus(i.status || 'OPEN')}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {items.length === 0 && <div className="text-center py-12 text-slate-400">No incidents recorded</div>}
      </div>
    </div>
  );
}

// RPT-04: Employee/HR Report
function HRReport() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    api.get('/reports/hr-report').then(r => setData(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loader />;
  if (!data) return <Empty />;

  const emps = (data.employees || []).filter(e => {
    if (filterType && e.type !== filterType) return false;
    if (search && !e.name?.toLowerCase().includes(search.toLowerCase()) && !e.employee_code?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });
  const empTypes = [...new Set((data.employees || []).map(e => e.type))];

  return (
    <div className="space-y-4" data-testid="hr-report">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Active" value={data.summary.total_active} icon={Users} color="text-emerald-600" />
        <SummaryCard label="Attendance (P/A/HD)" value={`${data.summary.attendance_present}/${data.summary.attendance_absent}/${data.summary.attendance_half_day}`} icon={CheckCircle} />
        <SummaryCard label="Pending Salary" value={formatINRFull(data.summary.pending_salary_total)} icon={Clock} color="text-amber-600" />
        <SummaryCard label="Outstanding Advances" value={formatINRFull(data.summary.outstanding_advances)} icon={BarChart3} color="text-red-600" />
        <SummaryCard label="PPE Issued" value={data.summary.ppe_issued} icon={Package} />
      </div>

      {/* Headcount by type */}
      {data.headcount_by_type?.length > 0 && (
        <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="headcount-chart">
          <h3 className="text-sm font-semibold text-slate-900 mb-3">Headcount by Type</h3>
          <div className="flex flex-wrap gap-3">
            {data.headcount_by_type.map(h => (
              <div key={h.name} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2">
                <span className="text-xs text-slate-600">{formatStatus(h.name)}</span>
                <span className="text-sm font-bold text-[#1B5486]">{h.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="border border-slate-200 rounded-md pl-8 pr-3 py-2 text-sm outline-none w-48" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-emp-type">
          <option value="">All Types</option>
          {empTypes.map(t => <option key={t} value={t}>{formatStatus(t)}</option>)}
        </select>
        <button onClick={() => exportToCSV(emps, 'hr_report.csv')} data-testid="export-hr-csv"
          className="ml-auto text-sm text-emerald-600 hover:underline flex items-center gap-1"><Download size={14} /> Export Excel</button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead><tr className="border-b border-slate-100 bg-slate-50/50">
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Code</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Name</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Type</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Designation</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Joining</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Phone</th>
            <th className="text-left py-2.5 px-4 text-xs uppercase text-slate-500">Status</th>
          </tr></thead>
          <tbody>
            {emps.map(e => (
              <tr key={e.id} className="border-b border-slate-50">
                <td className="py-2 px-4 font-mono text-xs text-[#1B5486]">{e.employee_code}</td>
                <td className="py-2 px-4 font-medium">{e.name}</td>
                <td className="py-2 px-4 text-xs">{formatStatus(e.type)}</td>
                <td className="py-2 px-4 text-slate-600">{e.designation || '-'}</td>
                <td className="py-2 px-4">{formatDate(e.joining_date)}</td>
                <td className="py-2 px-4 text-slate-500">{e.phone || '-'}</td>
                <td className="py-2 px-4"><span className={`text-xs px-1.5 py-0.5 rounded ${e.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{e.is_active ? 'Active' : 'Inactive'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {emps.length === 0 && <div className="text-center py-12 text-slate-400">No employees found</div>}
      </div>
    </div>
  );
}

function Loader() { return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#1B5486] border-t-transparent rounded-full" /></div>; }
function Empty() { return <div className="text-center py-12 text-slate-400">No data available</div>; }


function POInvoiceReport() {
  const [subTab, setSubTab] = useState('po-summary');
  const [poSummary, setPOSummary] = useState(null);
  const [invRegister, setInvRegister] = useState(null);
  const [ageing, setAgeing] = useState(null);
  const [tdsRegister, setTdsRegister] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const endpoint = subTab === 'po-summary' ? '/po-invoices/reports/po-summary' :
                     subTab === 'inv-register' ? '/po-invoices/reports/invoice-register' :
                     subTab === 'ageing' ? '/po-invoices/reports/receivables-ageing' :
                     '/po-invoices/reports/tds-register';
    api.get(endpoint).then(r => {
      if (subTab === 'po-summary') setPOSummary(r.data);
      else if (subTab === 'inv-register') setInvRegister(r.data);
      else if (subTab === 'ageing') setAgeing(r.data);
      else setTdsRegister(r.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [subTab]);

  const subTabs = [
    { id: 'po-summary', label: 'PO Summary' },
    { id: 'inv-register', label: 'Invoice Register' },
    { id: 'ageing', label: 'Receivables Ageing' },
    { id: 'tds', label: 'TDS Register' },
  ];

  return (
    <div data-testid="po-invoice-report" className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setSubTab(t.id)} data-testid={`poi-subtab-${t.id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              subTab === t.id ? 'bg-[#1B5486] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}>{t.label}</button>
        ))}
      </div>

      {loading ? <Loader /> : (
        <>
          {subTab === 'po-summary' && poSummary && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-600">{poSummary.total_pos} POs | Total: {formatINRFull(poSummary.total_value)}</p>
                <button onClick={() => exportToCSV(poSummary.items, 'po_summary')} className="text-xs text-emerald-600 hover:underline flex items-center gap-1" data-testid="export-po-summary"><Download size={12} /> Excel</button>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Project</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Supply POs</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Supply Value</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Service POs</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Service Value</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Value</th>
                  </tr></thead>
                  <tbody>
                    {poSummary.items.map((item, idx) => (
                      <tr key={item.project_code || idx} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 px-3"><span className="font-mono text-xs text-[#1B5486]">{item.project_code}</span> <span className="text-slate-600">{item.project_name}</span></td>
                        <td className="py-2 px-3 text-right">{item.supply_count}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatINRFull(item.supply_value)}</td>
                        <td className="py-2 px-3 text-right">{item.service_count}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatINRFull(item.service_value)}</td>
                        <td className="py-2 px-3 text-right font-mono font-medium">{formatINRFull(item.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {poSummary.items.length === 0 && <Empty />}
              </div>
            </div>
          )}

          {subTab === 'inv-register' && invRegister && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-600">{invRegister.items.length} invoices</p>
                <button onClick={() => exportToCSV(invRegister.items, 'invoice_register')} className="text-xs text-emerald-600 hover:underline flex items-center gap-1" data-testid="export-inv-register"><Download size={12} /> Excel</button>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice #</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Project</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Type</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Total</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">TDS</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Net</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Payment</th>
                  </tr></thead>
                  <tbody>
                    {invRegister.items.map((inv, idx) => (
                      <tr key={inv.invoice_number || idx} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-xs text-emerald-700">{inv.invoice_number}</td>
                        <td className="py-2 px-3 text-xs">{inv.project_code}</td>
                        <td className="py-2 px-3 text-slate-500">{formatDate(inv.invoice_date)}</td>
                        <td className="py-2 px-3"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{inv.invoice_type}</span></td>
                        <td className="py-2 px-3 text-right font-mono">{formatINRFull(inv.total_amount)}</td>
                        <td className="py-2 px-3 text-right font-mono text-red-500">{formatINRFull(inv.tds_deducted)}</td>
                        <td className="py-2 px-3 text-right font-mono text-emerald-600">{formatINRFull(inv.net_receivable)}</td>
                        <td className="py-2 px-3"><span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          inv.status === 'PAYMENT_RECEIVED' ? 'bg-green-100 text-green-700' :
                          inv.status === 'ACKNOWLEDGED' ? 'bg-amber-100 text-amber-700' :
                          inv.status === 'SUBMITTED' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                        }`}>{formatStatus(inv.status)}</span></td>
                        <td className="py-2 px-3 text-right font-mono">{inv.payment_amount ? formatINRFull(inv.payment_amount) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {invRegister.items.length === 0 && <Empty />}
              </div>
            </div>
          )}

          {subTab === 'ageing' && ageing && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-600">Total Outstanding: {formatINRFull(ageing.total_outstanding)}</p>
                <button onClick={() => exportToCSV(ageing.items, 'receivables_ageing')} className="text-xs text-emerald-600 hover:underline flex items-center gap-1" data-testid="export-ageing"><Download size={12} /> Excel</button>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-4">
                {Object.entries(ageing.buckets).map(([bucket, amount]) => (
                  <div key={bucket} className={`rounded-lg p-3 text-center ${
                    bucket === '0-30' ? 'bg-green-50' : bucket === '30-60' ? 'bg-amber-50' : bucket === '60-90' ? 'bg-orange-50' : 'bg-red-50'
                  }`} data-testid={`ageing-bucket-${bucket}`}>
                    <p className={`text-xs mb-1 font-semibold ${
                      bucket === '0-30' ? 'text-green-600' : bucket === '30-60' ? 'text-amber-600' : bucket === '60-90' ? 'text-orange-600' : 'text-red-600'
                    }`}>{bucket} Days</p>
                    <p className="text-lg font-semibold font-mono text-slate-900">{formatINRFull(amount)}</p>
                  </div>
                ))}
              </div>
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice #</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Project</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Total</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Outstanding</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Days</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Bucket</th>
                  </tr></thead>
                  <tbody>
                    {ageing.items.map((item, idx) => (
                      <tr key={item.invoice_number || idx} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-xs text-emerald-700">{item.invoice_number}</td>
                        <td className="py-2 px-3 text-xs">{item.project_code}</td>
                        <td className="py-2 px-3 text-slate-500">{formatDate(item.invoice_date)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatINRFull(item.total_amount)}</td>
                        <td className="py-2 px-3 text-right font-mono font-medium">{formatINRFull(item.outstanding)}</td>
                        <td className="py-2 px-3 text-right">{item.days}</td>
                        <td className="py-2 px-3"><span className={`text-xs px-2 py-0.5 rounded-full ${
                          item.bucket === '0-30' ? 'bg-green-100 text-green-700' :
                          item.bucket === '30-60' ? 'bg-amber-100 text-amber-700' :
                          item.bucket === '60-90' ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700'
                        }`}>{item.bucket}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {ageing.items.length === 0 && <Empty />}
              </div>
            </div>
          )}

          {subTab === 'tds' && tdsRegister && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-600">Total TDS: {formatINRFull(tdsRegister.total_tds)}</p>
                <button onClick={() => exportToCSV(tdsRegister.items, 'tds_register')} className="text-xs text-emerald-600 hover:underline flex items-center gap-1" data-testid="export-tds-register"><Download size={12} /> Excel</button>
              </div>
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead><tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice #</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Project</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Client</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Invoice Date</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Total Amount</th>
                    <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-semibold">TDS Deducted</th>
                    <th className="text-left py-2 px-3 text-xs uppercase tracking-wider text-slate-500">Status</th>
                  </tr></thead>
                  <tbody>
                    {tdsRegister.items.map((item, idx) => (
                      <tr key={item.invoice_number || idx} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="py-2 px-3 font-mono text-xs text-emerald-700">{item.invoice_number}</td>
                        <td className="py-2 px-3 text-xs">{item.project_code}</td>
                        <td className="py-2 px-3">{item.client_name || '-'}</td>
                        <td className="py-2 px-3 text-slate-500">{formatDate(item.invoice_date)}</td>
                        <td className="py-2 px-3 text-right font-mono">{formatINRFull(item.total_amount)}</td>
                        <td className="py-2 px-3 text-right font-mono font-medium text-red-600">{formatINRFull(item.tds_deducted)}</td>
                        <td className="py-2 px-3"><span className={`text-xs px-1.5 py-0.5 rounded-full ${
                          item.status === 'PAYMENT_RECEIVED' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                        }`}>{formatStatus(item.status)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {tdsRegister.items.length === 0 && <Empty />}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
